import { useAuthorityContext } from 'Services/common/Features/Authority/Context/authorityContext'
import { useAppDispatch } from 'Store'
import { resetAllApiState } from 'Util/ApiClient'
import { colors } from 'Util/colors'
import { PTA_CHANGE_REDIRECT_BASE_PATHS, SELECTED_PTA_STORAGE_KEY } from 'Util/constants'
import {
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  type SelectChangeEvent,
  styled
} from '@mui/material'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { t } from 'ttag'

interface PTASelectProps {
  isMenuBarSelect?: boolean
}

const StyledSelect = styled(Select, {
  shouldForwardProp: prop => prop !== 'isMenuBarSelect'
})<{ isMenuBarSelect?: boolean }>(({ theme, isMenuBarSelect }) => ({
  ...(isMenuBarSelect
    ? {
        minWidth: 100,
        fontSize: 14,
        fontWeight: 600,
        color: colors.textPrimaryDark,
        '& .MuiSelect-icon': {
          color: colors.textPrimaryDark
        }
      }
    : {
        backgroundColor: theme.palette.background.default
      })
}))

const StyledInputLabel = styled(InputLabel, {
  shouldForwardProp: prop => prop !== 'isMenuBarSelect'
})<{ isMenuBarSelect?: boolean }>(({ isMenuBarSelect }) => ({
  ...(isMenuBarSelect && {
    fontSize: 14,
    color: colors.walttiViolet[300],
    '&.Mui-focused': {
      color: colors.walttiViolet[300]
    }
  })
}))

/**
 * Given the current pathname, returns the appropriate base path to redirect to
 * when PTA selection changes. If the user is on a sub-route of a known base path,
 * returns that base path. Otherwise returns the current pathname (no redirect needed).
 */
function getRedirectPathOnPtaChange(currentPathname: string): string {
  for (const basePath of PTA_CHANGE_REDIRECT_BASE_PATHS) {
    if (currentPathname.startsWith(basePath) && currentPathname !== basePath) {
      return basePath
    }
  }

  return currentPathname
}

export default function PTASelect({ isMenuBarSelect = false }: Readonly<PTASelectProps>) {
  const { codespaces } = useAuthorityContext()
  const location = useLocation()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()

  const handlePTASelectChange = (event: SelectChangeEvent<unknown>, _child: React.ReactNode) => {
    const { value } = event.target
    if (value === '') {
      localStorage.removeItem(SELECTED_PTA_STORAGE_KEY)
    } else {
      localStorage.setItem(SELECTED_PTA_STORAGE_KEY, value as string)
    }
    resetAllApiState(dispatch)
    const redirectPath = getRedirectPathOnPtaChange(location.pathname)
    navigate(redirectPath, { replace: true })
  }

  /**
   * Effect to reset selected PTA if it no longer exists in the codespaces list.
   */
  useEffect(() => {
    const userPta = localStorage.getItem(SELECTED_PTA_STORAGE_KEY) ?? ''
    if (
      userPta &&
      codespaces.length > 0 &&
      !codespaces.some(codespace => codespace.codespace_id === userPta)
    ) {
      localStorage.removeItem(SELECTED_PTA_STORAGE_KEY)
    }
  }, [codespaces])

  return (
    <FormControl fullWidth={!isMenuBarSelect} size={isMenuBarSelect ? 'small' : 'medium'}>
      <StyledInputLabel shrink isMenuBarSelect={isMenuBarSelect}>{t`Authority`}</StyledInputLabel>
      <StyledSelect
        value={localStorage.getItem(SELECTED_PTA_STORAGE_KEY) ?? ''}
        onChange={handlePTASelectChange}
        label={t`Authority`}
        displayEmpty
        size={isMenuBarSelect ? 'small' : 'medium'}
        isMenuBarSelect={isMenuBarSelect}
      >
        <MenuItem value="">{t`Not selected`}</MenuItem>
        {codespaces.map(codespace => (
          <MenuItem key={codespace.id} value={codespace.codespace_id}>
            {codespace.description}
          </MenuItem>
        ))}
      </StyledSelect>
    </FormControl>
  )
}
